import { useEffect, useState } from "react";
import { Logo } from "@/components/layout/AppShell";
import { Button, Input, Field, Spinner } from "@/components/ui";
import { supabase } from "@/lib/supabase";
import { Lock, CheckCircle2, AlertCircle } from "lucide-react";

// =============================================================================
// ResetPasswordScreen — handles both Supabase invite + password recovery flows.
//
// When a user clicks a Supabase auth email link (invite OR password reset),
// Supabase redirects them to this app with auth tokens in the URL hash:
//   https://ops.coretelgroup.com/reset-password#access_token=...&type=recovery
//
// Supabase JS client auto-detects the hash and sets a temporary session.
// In that session the user can call updateUser({password}) to set/change
// their password. After that they have a real session and we redirect home.
//
// The route is mounted in App.tsx OUTSIDE the auth gate so it works whether
// or not the auth bootstrap has loaded the public.users row yet.
//
// Two failure modes worth handling:
//   1. The user lands here without any session at all — link expired,
//      already used, or someone navigated here manually. Show a clear
//      "request a new link" message and link back to /login.
//   2. updateUser fails (network, weak password, etc) — show the error
//      inline and let them retry.
// =============================================================================

const MIN_LEN = 8;

export default function ResetPasswordScreen() {
  const [password,    setPassword]    = useState("");
  const [confirm,     setConfirm]     = useState("");
  const [submitting,  setSubmitting]  = useState(false);
  const [error,       setError]       = useState<string | null>(null);
  const [done,        setDone]        = useState(false);

  // Tri-state: still checking, has session, no session.
  const [authState, setAuthState] = useState<"checking" | "ok" | "missing">("checking");

  // On mount: poll for a session. Supabase JS auto-detects the URL hash on
  // page load (detectSessionInUrl: true is the default), but the timing
  // can be off relative to React mount. Listen for state changes too so
  // we catch the session if it arrives a beat after we render.
  useEffect(() => {
    let cancelled = false;

    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setAuthState(data.session ? "ok" : "missing");
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      // PASSWORD_RECOVERY fires when the recovery token is exchanged.
      // SIGNED_IN fires for invite links (which give a real session).
      if (session) setAuthState("ok");
    });

    return () => { cancelled = true; sub.subscription.unsubscribe(); };
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (password.length < MIN_LEN) {
      setError(`Password must be at least ${MIN_LEN} characters.`);
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }

    setSubmitting(true);
    const { error: updateErr } = await supabase.auth.updateUser({ password });
    if (updateErr) {
      setError(updateErr.message);
      setSubmitting(false);
      return;
    }

    setDone(true);
    // Brief success state, then full reload to home so AuthBootstrap re-runs
    // cleanly with the new session and pulls the public.users row.
    setTimeout(() => { window.location.href = "/"; }, 1500);
  }

  return (
    <div className="min-h-screen bg-ink text-text flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-10">
          <Logo size={40} showText={false} />
          <div className="mt-4 text-text-muted text-[11px] uppercase tracking-[0.2em]">
            Set your password
          </div>
        </div>

        {authState === "checking" && (
          <div className="card p-6 flex items-center justify-center">
            <Spinner size={20}/>
          </div>
        )}

        {authState === "missing" && (
          <div className="card p-6 space-y-3">
            <div className="flex items-start gap-2 text-warn">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0"/>
              <div className="text-[13px] leading-snug">
                This link has expired or has already been used.
              </div>
            </div>
            <p className="text-[12px] text-text-muted leading-snug">
              Password reset and invite links are single-use and expire after a short window.
              Head back to the login screen and use <span className="font-medium">Forgot password</span>,
              or ask a director to resend you an invite.
            </p>
            <Button
              variant="primary"
              onClick={() => { window.location.href = "/"; }}
              className="w-full"
            >
              Back to login
            </Button>
          </div>
        )}

        {authState === "ok" && !done && (
          <form onSubmit={handleSubmit} className="card p-6 space-y-4">
            <p className="text-[12px] text-text-muted">
              Pick a password to use from now on. Minimum {MIN_LEN} characters.
            </p>
            <Field label="New password">
              <Input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="new-password"
                placeholder="••••••••"
                required
                autoFocus
                disabled={submitting}
              />
            </Field>
            <Field label="Confirm password">
              <Input
                type="password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                autoComplete="new-password"
                placeholder="••••••••"
                required
                disabled={submitting}
              />
            </Field>

            {error && (
              <div className="flex items-start gap-2 text-lost text-[12px] leading-snug">
                <AlertCircle size={14} className="mt-0.5 flex-shrink-0"/>
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              icon={Lock}
              disabled={submitting || !password || !confirm}
              className="w-full"
            >
              {submitting ? "Setting password…" : "Set password"}
            </Button>
          </form>
        )}

        {authState === "ok" && done && (
          <div className="card p-6 space-y-3">
            <div className="flex items-center gap-2 text-ok">
              <CheckCircle2 size={18}/>
              <div className="text-[14px] font-medium">Password set</div>
            </div>
            <p className="text-[12px] text-text-muted">
              Taking you to the app…
            </p>
            <div className="flex justify-center pt-2">
              <Spinner size={16}/>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
